import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle2, Factory, Truck, XCircle, Mail, Printer, FileEdit, ChevronsUpDown, Check, User, MapPin, Phone, FileText as FileTextIcon, Download } from "lucide-react";
import { toast } from "sonner";
import { useOrderStock, useOneOrderStock, useStockAvailability, OrderStockBadge, OrderStockPanel, StockHint } from "@/components/OrderStock";
import { PartialDeliveryEditor, PartialDeliveryList, partialDeliveryError, partialFromLine, toPartialPayload } from "@/components/PartialDeliveryEditor";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import RevisionHistoryButton from "@/components/RevisionHistory";
import { revisionNo } from "@/lib/revisionNo";
import { formatMoney, docCurrency, currencySymbol } from "@/lib/currency";
import { CurrencySelect } from "@/components/CurrencySelect";
import OrderEmailStatus from "@/components/OrderEmailStatus";

const STATUS = {
  draft: "bg-slate-100 text-slate-600",
  order_raised: "bg-orange-50 text-orange-700",
  confirmed: "bg-blue-50 text-blue-700",
  in_production: "bg-amber-50 text-amber-700",
  ready: "bg-indigo-50 text-indigo-700",
  delivered: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
  revised: "bg-purple-50 text-purple-700",
};

const UOMS = ["PCS", "KG", "MTR", "SET", "BOX", "NOS", "LTR"];

// Compose a multi-line address string from a customer's structured fields.
const composeAddress = (c, kind = "billing") => {
  if (!c) return "";
  const ship = kind === "shipping" && !c.same_as_billing;
  const p = ship ? "shipping_" : "billing_";
  const lines = [c[`${p}line1`], c[`${p}line2`]].filter(Boolean);
  const tail = [c[`${p}city`], c[`${p}state`], c[`${p}pin`]].filter(Boolean).join(", ");
  const country = c[`${p}country`];
  const out = [lines.join(", "), tail, country].filter(Boolean).join("\n");
  // Fall back to legacy single-line address if structured fields are empty
  return out || c.address || "";
};

const emptyLine = (firstFgId = "") => ({
  item_id: firstFgId, qty: 1, price: 0,
  hsn_code: "", uom: "PCS",
  discount_pct: 0, cgst_pct: 9, sgst_pct: 9, igst_pct: 0,
  delivery_time: "",
});

const emptyForm = (firstCustId = "", firstFgId = "") => ({
  customer_id: firstCustId,
  // A Transfer Order never blocks stock itself — its Sales Order does. On
  // edit this mirrors whether the Sales Order has the stock blocked, so the
  // line hints apply the stock check only when it does.
  stock_blocked: false,
  po_reference: "",
  notes: "",
  currency: "INR",       // document currency — rates/totals are in it (no conversion)
  expected_delivery_date: "",
  billing_address: "",
  shipping_address: "",
  same_as_billing: true,
  customer_service: { contact_person: "", email: "", phone: "" },
  additional_charges: 0,
  additional_charges_label: "Freight / Other",
  header_discount: 0,
  terms_id: "",
  bank_id: "",
  issuer_id: "",
  lines: [emptyLine(firstFgId)],
});

// Helpers used by the form to split a total GST% based on customer/company state
const classifyGst = (custState, companyState) => {
  const a = (custState || "").trim().toLowerCase();
  const b = (companyState || "").trim().toLowerCase();
  if (!a || !b) return "unknown";
  return a === b ? "intra" : "inter";
};
const splitGstPct = (gstType, totalPct) => {
  const g = Number(totalPct || 0);
  if (gstType === "inter") return { cgst_pct: 0, sgst_pct: 0, igst_pct: g };
  const half = Math.round((g / 2) * 100) / 100;
  return { cgst_pct: half, sgst_pct: half, igst_pct: 0 };
};

export default function SalesOrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [terms, setTerms] = useState([]);
  const [banks, setBanks] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [companyState, setCompanyState] = useState("");
  const [users, setUsers] = useState([]);  // for Issuer selector (admin only — falls back to current user)
  const [selected, setSelected] = useState(null);
  const [selectedTotals, setSelectedTotals] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Revise TO reuses the edit sheet: same pre-filled details, but Save
  // issues the next revision (status unchanged) instead of a plain edit.
  const [revising, setRevising] = useState(null);
  const [revisionReason, setRevisionReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Deleted Transfer Orders lookup (admin-only) — "where did TO-XXXX go?"
  // Backs onto GET /sales-orders/deletions, the audit trail written the
  // moment a Transfer Order is deleted. Only finds deletions that happened
  // after that audit trail existed — it can't look further back than that.
  const [deletionsOpen, setDeletionsOpen] = useState(false);
  const [deletionsQuery, setDeletionsQuery] = useState("");
  const [deletionsBusy, setDeletionsBusy] = useState(false);
  const [deletionsSearched, setDeletionsSearched] = useState(false);
  const [deletionsResults, setDeletionsResults] = useState([]);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailPayload, setEmailPayload] = useState({ to_email: "", cc_email: "", bcc_email: "", subject: "", message: "" });
  // Email history/audit trail (Requirement 7) — every Send Email attempt for
  // the selected Transfer Order, success or failure. The Send Email button
  // itself never hides, disables, or turns into "Email Sent"; this list is
  // how a resend's outcome is confirmed without that button changing state.
  const [emailHistory, setEmailHistory] = useState([]);
  // Per-row "Check delivery" result: what the mail provider says happened
  // after it accepted the email (delivered / bounced / handed to mailbox).
  const [emailDelivery, setEmailDelivery] = useState({});
  const [custPickerOpen, setCustPickerOpen] = useState(false);

  const fgItems = useMemo(() => items.filter((i) => i.type !== "RAW"), [items]);
  const custMap = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  // This record is raised as a Transfer Order (stored as SO-XXXX) and becomes a
  // Sales Order once Production approves it — the Sales Order carries a
  // reference back to the Transfer Order it was raised against, shown as
  // TO-XXXX (display-only prefix swap; the stored code never changes).
  const toNo = (code) => (code && code.startsWith("SO-") ? `TO-${code.slice(3)}` : code || "—");

  const canConfirm = ["admin", "planner"].includes(user?.role);
  // Revise / Cancel TO — these roles on any TO, plus the marketing user who
  // raised it on their own. Both close once packing is completed (backend
  // stamps `packing_locked`).
  const ownTO = (so) => user?.role === "marketing" && so?.created_by === user?.email;
  const canRevise = (so) => ["admin", "planner", "production_manager"].includes(user?.role) || ownTO(so);
  const canCancel = (so) => user?.role === "admin" || ownTO(so);

  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [s, c, i, t, b, g, cs, u] = await Promise.all([
      api.get("/sales-orders"),
      api.get("/customers"),
      api.get("/items"),
      api.get("/terms").catch(() => ({ data: [] })),
      api.get("/banks").catch(() => ({ data: [] })),
      api.get("/gst-rates").catch(() => ({ data: [] })),
      api.get("/settings/company").catch(() => ({ data: { state: "" } })),
      user?.role === "admin"
        ? api.get("/users").catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),  // non-admin roles can't list users; skip 403 noise
    ]);
    setList(s.data);
    setCustomers(c.data);
    setItems(i.data);
    setTerms(t.data || []);
    setBanks(b.data || []);
    setGstRates((g.data || []).filter((x) => x.status !== "inactive"));
    setCompanyState(cs.data?.state || "");
    setUsers(u.data || []);
    if (selected) {
      const found = s.data.find((x) => x.id === selected.id);
      setSelected(found || null);
    }
  };

  useEffect(() => { load(); }, []);

  // Deep-link support — e.g. "View Transfer Order" from the Sales Order page.
  useEffect(() => {
    const id = location.state?.selectId;
    if (id && list.length) {
      const found = list.find((x) => x.id === id);
      if (found) setSelected(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectId, list.length]);

  // Order date shown on each row / used by the date filter — the day the
  // Transfer Order was raised (created_at), not so_date (that's stamped
  // later, on Production Approval, and is empty for most orders in the list).
  const orderDate = (so) => so.created_at || null;

  // True when an order's date falls within the selected [dateFrom, dateTo]
  // range (either side optional). Compared as plain yyyy-mm-dd strings so it
  // matches what the <input type="date"> filters show, regardless of the
  // viewer's timezone offset from the stored UTC timestamp.
  const matchesDateFilter = (so) => {
    if (!dateFrom && !dateTo) return true;
    const raw = orderDate(so);
    if (!raw) return false;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return false;
    const day = d.toISOString().slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  };

  // Search + date filter
  // Stock reserved against each order + live availability for the form lines.
  const [stockTick, setStockTick] = useState(0);
  const selectedStock = useOneOrderStock(selected ? selected.id : null, stockTick);
  const orderStock = useOrderStock(list.map((so) => so.id), list, stockTick);
  const formStock = useStockAvailability(form.lines.map((l) => l.item_id), editingId);
  const formRequested = useMemo(() => {
    const m = {};
    form.lines.forEach((l) => { if (l.item_id) m[l.item_id] = (m[l.item_id] || 0) + Number(l.qty || 0); });
    return m;
  }, [form.lines]);
  const warnStock = (data) => {
    if (data?.stock_warnings?.length) {
      toast.warning("Blocked — awaiting stock", {
        description: data.stock_warnings.map((w) => `${w.item_code}: requested ${w.requested_qty}, in stock for it ${w.available_qty}, awaiting ${Math.round((w.requested_qty - w.available_qty) * 1000) / 1000}`).join(" | "),
      });
    }
  };

  const visibleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((so) => {
      if (!matchesDateFilter(so)) return false;
      if (!q) return true;
      // The API stamps customer_name/customer_code onto every order the caller
      // can see (see _attach_customer_names on the backend) — prefer that over
      // custMap, which is privacy-scoped to customers the current user owns and
      // won't have an entry for another rep's customer.
      const cust = custMap[so.customer_id];
      const custName = so.customer_name || cust?.name || "";
      const custCode = so.customer_code || cust?.code || "";
      const inItems = (so.lines || []).some((l) => {
        const it = itemMap[l.item_id];
        return it && (
          (it.code || "").toLowerCase().includes(q) ||
          (it.name || "").toLowerCase().includes(q)
        );
      });
      return (
        (so.code || "").toLowerCase().includes(q) ||
        toNo(so.code).toLowerCase().includes(q) ||
        (so.po_reference || "").toLowerCase().includes(q) ||
        (so.status || "").toLowerCase().includes(q) ||
        custName.toLowerCase().includes(q) ||
        custCode.toLowerCase().includes(q) ||
        inItems
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, search, custMap, itemMap, dateFrom, dateTo]);

  // Live totals while filling the form
  const formTotals = useMemo(() => {
    let subtotal = 0, cgst = 0, sgst = 0, igst = 0, discount = 0;
    for (const ln of form.lines) {
      const gross = Number(ln.qty || 0) * Number(ln.price || 0);
      const disc = gross * Number(ln.discount_pct || 0) / 100;
      const taxable = gross - disc;
      subtotal += taxable;
      discount += disc;
      cgst += taxable * Number(ln.cgst_pct || 0) / 100;
      sgst += taxable * Number(ln.sgst_pct || 0) / 100;
      igst += taxable * Number(ln.igst_pct || 0) / 100;
    }
    const charges = Number(form.additional_charges || 0);
    const headerDisc = Number(form.header_discount || 0);
    const pre = subtotal + cgst + sgst + igst + charges - headerDisc;
    // INR rounds to the nearest rupee (as before); USD keeps cents — same as the backend.
    const grand = form.currency === "USD" ? Math.round(pre * 100) / 100 : Math.round(pre);
    return { subtotal, cgst, sgst, igst, discount, charges, headerDisc, grand, roundOff: grand - pre };
  }, [form]);

  // Fetch totals when selected changes
  useEffect(() => {
    if (!selected) { setSelectedTotals(null); return; }
    api.get(`/sales-orders/${selected.id}/totals`).then((r) => setSelectedTotals(r.data)).catch(() => setSelectedTotals(null));
  }, [selected]);

  const addLine = () => setForm((f) => ({
    ...f,
    lines: [...f.lines, emptyLine(fgItems[0]?.id || "")],
  }));
  const setLine = (i, p) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...p } : l) }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  // GST type derived from the picked customer's billing state and the company state
  const formGstType = useMemo(() => {
    const c = custMap[form.customer_id];
    return classifyGst(c?.billing_state, companyState);
  }, [form.customer_id, custMap, companyState]);

  // Apply a GST rate (from the master) to a single line, splitting based on intra/inter
  const applyGstRateToLine = (lineIdx, totalPct) => {
    const split = splitGstPct(formGstType, totalPct);
    setLine(lineIdx, split);
  };
  // Apply a rate to ALL lines (header convenience action)
  const applyGstRateToAll = (totalPct) => {
    const split = splitGstPct(formGstType, totalPct);
    setForm((f) => ({ ...f, lines: f.lines.map((l) => ({ ...l, ...split })) }));
  };

  const openNew = () => {
    setEditingId(null);
    const firstCust = customers[0];
    const fresh = emptyForm(firstCust?.id || "", fgItems[0]?.id || "");
    if (firstCust) {
      fresh.billing_address = composeAddress(firstCust, "billing");
      fresh.shipping_address = composeAddress(firstCust, "shipping");
      fresh.same_as_billing = firstCust.same_as_billing ?? true;
      fresh.customer_service = {
        contact_person: firstCust.contact || "",
        email: firstCust.email || "",
        phone: firstCust.phone || "",
      };
    }
    fresh.issuer_id = user?.id || "";
    setForm(fresh);
    setOpen(true);
  };

  const openEdit = (so, revise = false) => {
    setEditingId(so.id);
    setRevising(revise ? so : null);
    setRevisionReason("");
    setForm({
      customer_id: so.customer_id,
      // Blocked only via its Sales Order — the server reports that state.
      stock_blocked: orderStock[so.id]?.stock_blocked === true,
      po_reference: so.po_reference || "",
      notes: so.notes || "",
      currency: docCurrency(so),
      // Locked once Production has approved it (its Sales Order is raised).
      currency_locked: !!so.production_approved_at,
      expected_delivery_date: so.expected_delivery_date ? new Date(so.expected_delivery_date).toISOString().slice(0, 10) : "",
      billing_address: so.billing_address || "",
      shipping_address: so.shipping_address || "",
      same_as_billing: so.same_as_billing ?? true,
      customer_service: so.customer_service || { contact_person: "", email: "", phone: "" },
      additional_charges: so.additional_charges || 0,
      additional_charges_label: so.additional_charges_label || "Freight / Other",
      header_discount: so.header_discount || 0,
      terms_id: so.terms_id || "",
      bank_id: so.bank_id || "",
      issuer_id: so.issuer_id || user?.id || "",
      lines: (so.lines || []).map((l) => ({
        item_id: l.item_id, qty: l.qty, price: l.price,
        hsn_code: l.hsn_code || "", uom: l.uom || "PCS",
        discount_pct: l.discount_pct || 0,
        cgst_pct: l.cgst_pct || 0, sgst_pct: l.sgst_pct || 0, igst_pct: l.igst_pct || 0,
        delivery_time: l.delivery_time || "",
        delivery_date: l.delivery_date ? String(l.delivery_date).slice(0, 10) : "",
        ...partialFromLine(l),
      })),
    });
    setOpen(true);
  };

  // Auto-fill billing address when customer changes
  const onCustomerChange = (cid) => {
    const c = custMap[cid] || customers.find((x) => x.id === cid);
    const newGstType = classifyGst(c?.billing_state, companyState);
    setForm((f) => ({
      ...f,
      customer_id: cid,
      billing_address: composeAddress(c, "billing"),
      shipping_address: composeAddress(c, "shipping"),
      same_as_billing: c?.same_as_billing ?? f.same_as_billing,
      customer_service: {
        contact_person: c?.contact || f.customer_service.contact_person,
        email: c?.email || f.customer_service.email,
        phone: c?.phone || f.customer_service.phone,
      },
      // Re-split each line's GST based on the new state classification
      lines: f.lines.map((l) => {
        const totalGst = (Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0));
        return { ...l, ...splitGstPct(newGstType, totalGst) };
      }),
    }));
    setCustPickerOpen(false);
  };

  const submit = async () => {
    if (busy) return;
    if (!form.customer_id) return toast.error("Pick a customer");
    if (form.lines.some((l) => !l.item_id || Number(l.qty || 0) <= 0)) return toast.error("Each line needs item + qty");
    const partialErr = form.lines.map(partialDeliveryError).find(Boolean);
    if (partialErr) return toast.error(partialErr);
    setBusy(true);
    try {
      // Line number fields are kept as raw strings while the user is typing
      // (so a trailing "." or a cleared field isn't clobbered mid-edit —
      // see the line-item inputs above); coerce them to numbers here, once,
      // right before they leave the form.
      const normalizedLines = form.lines.map((l) => ({
        ...l,
        qty: Number(l.qty || 0),
        price: Number(l.price || 0),
        discount_pct: Number(l.discount_pct || 0),
        cgst_pct: Number(l.cgst_pct || 0),
        sgst_pct: Number(l.sgst_pct || 0),
        igst_pct: Number(l.igst_pct || 0),
        delivery_date: l.delivery_date ? new Date(l.delivery_date).toISOString() : null,
        ...toPartialPayload(l),
      }));
      const { currency_locked, ...formFields } = form;
      const payload = {
        ...formFields,
        lines: normalizedLines,
        additional_charges: Number(form.additional_charges || 0),
        header_discount: Number(form.header_discount || 0),
        expected_delivery_date: form.expected_delivery_date ? new Date(form.expected_delivery_date).toISOString() : null,
        terms_id: form.terms_id || null,
        bank_id: form.bank_id || null,
      };
      if (editingId) {
        const { lines, expected_delivery_date, po_reference, notes, billing_address, shipping_address,
          same_as_billing, customer_service, additional_charges, additional_charges_label,
          header_discount, terms_id, bank_id, issuer_id, currency } = payload;
        const body = {
          lines, expected_delivery_date, po_reference, notes, billing_address, shipping_address,
          same_as_billing, customer_service, additional_charges, additional_charges_label,
          header_discount, terms_id, bank_id, issuer_id,
          ...(currency_locked ? {} : { currency }),
        };
        if (revising) {
          const { data } = await api.post(`/sales-orders/${editingId}/revise`, { ...body, reason: revisionReason });
          const em = data.auto_email;
          toast.success(`${toNo(data.code)} revised — ${data.revision_no || revisionNo(toNo(data.code), data.revision_number)}` +
            (data.customer_sales_order_code ? ` — Sales Order ${data.customer_sales_order_code} updated` : "") +
            (em?.sent ? " & emailed" : em?.queued ? " (email queued)" : ""));
          warnStock(data);
        } else {
          const { data } = await api.patch(`/sales-orders/${editingId}`, body);
          toast.success("Transfer Order updated");
          warnStock(data);
        }
      } else {
        const { data } = await api.post("/sales-orders", payload);
        toast.success("Transfer Order created");
        warnStock(data);
      }
      setOpen(false);
      setEditingId(null);
      setRevising(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const callAction = async (so, action, msg, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sales-orders/${so.id}/${action}`, body);
      if (action === "create-wo") {
        toast.success(`${data.count} work order(s) created`);
      } else {
        toast.success(msg);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const remove = async (so) => {
    if (!window.confirm(`Delete ${toNo(so.code)}?`)) return;
    try {
      await api.delete(`/sales-orders/${so.id}`);
      if (selected?.id === so.id) setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const checkEmailDelivery = async (emailId) => {
    try {
      const r = await api.get(`/sales-orders/${selected.id}/emails/${emailId}/delivery`);
      setEmailDelivery((d) => ({ ...d, [emailId]: r.data }));
    } catch (e) {
      setEmailDelivery((d) => ({ ...d, [emailId]: { delivery: "error", detail: formatApiError(e.response?.data?.detail) } }));
    }
  };

  const loadEmailHistory = async (soId) => {
    if (!soId) return setEmailHistory([]);
    try {
      const r = await api.get(`/sales-orders/${soId}/emails`);
      setEmailHistory(r.data || []);
    } catch {
      setEmailHistory([]);
    }
  };

  // Refresh the audit trail whenever a different Transfer Order is selected —
  // it survives refreshes/logout/etc. because it's read straight from the
  // server, not kept in local component state.
  useEffect(() => { loadEmailHistory(selected?.id); }, [selected?.id]);

  const searchDeletions = async () => {
    const code = deletionsQuery.trim();
    if (!code) return;
    setDeletionsBusy(true);
    setDeletionsSearched(false);
    try {
      const r = await api.get("/sales-orders/deletions", { params: { code } });
      setDeletionsResults(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not look up deleted orders");
      setDeletionsResults([]);
    } finally {
      setDeletionsSearched(true);
      setDeletionsBusy(false);
    }
  };

  const openEmail = () => {
    const cs = selected?.customer_service || {};
    const cust = custMap[selected?.customer_id] || {};
    // Auto-fill Cc with the Transfer Order creator's email — captured on
    // `created_by` at creation time from the logged-in user record. Only
    // pre-fill when it actually looks like an email (older/system-seeded
    // orders may carry a non-email placeholder there); either way it stays
    // a plain editable field, so the user can change or clear it.
    const creatorEmail = selected?.created_by && selected.created_by.includes("@") ? selected.created_by : "";
    setEmailPayload({
      to_email: cs.email || cust.email || "",
      cc_email: creatorEmail,
      bcc_email: "",
      subject: `Transfer Order ${toNo(selected.code)} from Canares Automation`,
      message: "Please find the attached Transfer Order. Reply to this email for any clarifications.",
    });
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!emailPayload.to_email) return toast.error("Recipient email is required");
    setBusy(true);
    try {
      await api.post(`/sales-orders/${selected.id}/email`, {
        to_email: emailPayload.to_email,
        cc_email: emailPayload.cc_email || null,
        bcc_email: emailPayload.bcc_email || null,
        subject: emailPayload.subject || null,
        message: emailPayload.message || "",
      });
      toast.success(`Sent to ${emailPayload.to_email}`);
      setEmailOpen(false);
    } catch (e) {
      // Send Email stays available — no disabling, hiding, or "Email Sent"
      // swap — the user can just retry from the still-open dialog.
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally {
      setBusy(false);
      loadEmailHistory(selected?.id);
    }
  };

  const printSO = async () => {
    const r = await api.get(`/sales-orders/${selected.id}/render-html`, { responseType: "text" });
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${toNo(selected.code)}</title></head><body>${r.data}<script>window.onload=()=>setTimeout(()=>window.print(),200)</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="so-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Raise Transfer Orders
          </h1>
          <p className="text-sm text-slate-500 mt-1">GST-compliant transfer orders → confirm → produce → dispatch.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Button
              variant="outline"
              data-testid="check-deletions-btn"
              onClick={() => { setDeletionsOpen(true); setDeletionsQuery(""); setDeletionsResults([]); setDeletionsSearched(false); }}
            >
              <Trash2 className="w-4 h-4 mr-1.5" />Check Deleted Orders
            </Button>
          )}
          <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-so-btn" onClick={() => navigate("/raise-transfer-order")}>
            <Plus className="w-4 h-4 mr-1.5" />New Transfer Order
          </Button>
        </div>
      </div>

      {/* Deleted Transfer Orders lookup (admin) — "where did TO-XXXX go?" */}
      <Dialog open={deletionsOpen} onOpenChange={setDeletionsOpen}>
        <DialogContent data-testid="deletions-dialog">
          <DialogHeader>
            <DialogTitle>Check Deleted Transfer Orders</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Look up a Transfer Order number (e.g. <span className="mono">TO-5158</span>) to see if it was
            deleted, by whom, and when. This only finds deletions that happened after this audit
            feature was added — it can't look further back than that.
          </p>
          <div className="flex items-center gap-2">
            <Input
              placeholder="TO-5158 or SO-5158"
              value={deletionsQuery}
              onChange={(e) => setDeletionsQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") searchDeletions(); }}
              data-testid="deletions-search-input"
            />
            <Button onClick={searchDeletions} disabled={deletionsBusy || !deletionsQuery.trim()} data-testid="deletions-search-btn">
              {deletionsBusy ? "Searching…" : "Search"}
            </Button>
          </div>
          {deletionsSearched && (
            deletionsResults.length === 0 ? (
              <div className="text-sm text-slate-500 py-3" data-testid="deletions-no-match">
                No deletion record found for this code. Either it was never deleted (double-check the
                number), or it was deleted before this audit trail existed.
              </div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto" data-testid="deletions-results">
                {deletionsResults.map((d) => (
                  <div key={d.id} className="rounded-md border border-slate-200 p-3 text-sm">
                    <div className="font-semibold text-slate-800">{d.so_code}</div>
                    <div className="text-slate-600">Deleted by <span className="mono">{d.deleted_by}</span></div>
                    <div className="text-slate-500 text-xs">{d.deleted_at ? new Date(d.deleted_at).toLocaleString() : "—"}</div>
                    <div className="text-slate-500 text-xs mt-1">
                      Status when deleted: <span className="mono">{d.snapshot?.status || "—"}</span> · Customer:{" "}
                      <span className="mono">{d.snapshot?.customer_id || "—"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Sheet for create/edit */}
      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setRevising(null); } }}>
        <SheetContent className="w-full sm:max-w-4xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle data-testid="so-sheet-title">{revising ? `Revise ${revisionNo(toNo(revising.code), revising.revision_number)} → ${revisionNo(toNo(revising.code), (revising.revision_number || 0) + 1)}` : editingId ? "Edit Transfer Order" : "New Transfer Order"}</SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {revising && (
              <div className="rounded-md border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900 space-y-2" data-testid="so-revise-reason-box">
                <div>
                  Delivery dates follow the latest commitment: a quantity <strong>increase</strong> applies the new line delivery date
                  (never earlier than the current one); a quantity <strong>decrease</strong> keeps the current date.
                </div>
                <div>
                  <Label htmlFor="so-revise-reason">Revision reason</Label>
                  <Input id="so-revise-reason" value={revisionReason} onChange={(e) => setRevisionReason(e.target.value)}
                    placeholder="e.g. Customer increased quantity" data-testid="so-revise-reason" />
                </div>
              </div>
            )}
            <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-xs text-orange-800" data-testid="marketing-banner">
              <div className="font-semibold uppercase tracking-[0.14em] text-[10px] mb-1">Direct Handover Workflow</div>
              Every Transfer Order you submit here goes straight to <span className="mono font-semibold">Order Raised</span> in the
              Production Approval queue — bypassing draft/confirm. Production attaches the Production Date on approval, and a
              Work Order (inheriting Part Number, Quantity, and this Transfer Order ID) is auto-generated.
            </div>
            {/* Customer + dates */}
            <section className="grid grid-cols-2 gap-3">
              <div>
                <Label>Customer *</Label>
                <Popover open={custPickerOpen} onOpenChange={setCustPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={custPickerOpen}
                      className="w-full justify-between font-normal"
                      data-testid="so-cust-select"
                    >
                      {form.customer_id
                        ? `${custMap[form.customer_id]?.code || ""} — ${custMap[form.customer_id]?.name || ""}`
                        : "Search customer..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command>
                      <CommandInput placeholder="Type code, name, GSTIN, city..." data-testid="so-cust-search" />
                      <CommandList>
                        <CommandEmpty>No customer found.</CommandEmpty>
                        <CommandGroup>
                          {customers.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={`${c.code} ${c.name} ${c.gstin} ${c.billing_city || ""} ${c.contact || ""}`}
                              onSelect={() => onCustomerChange(c.id)}
                              data-testid={`so-cust-opt-${c.code}`}
                            >
                              <Check className={`mr-2 h-4 w-4 ${form.customer_id === c.id ? "opacity-100" : "opacity-0"}`} />
                              <div className="flex flex-col">
                                <span className="text-sm"><span className="mono text-xs text-slate-500">{c.code}</span> · {c.name}</span>
                                <span className="text-[11px] text-slate-500">{c.billing_city ? `${c.billing_city}, ${c.billing_state || ""}` : ""}{c.gstin ? ` · ${c.gstin}` : ""}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Customer PO Reference</Label>
                <Input value={form.po_reference} onChange={(e) => setForm({ ...form, po_reference: e.target.value })} />
              </div>
              <CurrencySelect
                value={form.currency}
                onChange={(v) => setForm((f) => ({ ...f, currency: v }))}
                hasAmounts={form.lines.some((l) => Number(l.price) > 0) || Number(form.additional_charges) > 0}
                disabled={!!form.currency_locked}
                hint={form.currency_locked ? "Locked — the Sales Order has been raised in this currency." : undefined}
                testid="so-form-currency"
              />
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal note" />
              </div>
            </section>

            {/* Read-only Customer Info card (auto-filled from master) */}
            {form.customer_id && custMap[form.customer_id] && (
              <section className="bg-slate-50/80 border border-slate-200 rounded-md p-4" data-testid="so-cust-info-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Customer Master · Auto-filled</div>
                  <button
                    type="button"
                    className="text-xs text-blue-700 hover:underline"
                    onClick={() => navigate("/customers")}
                    data-testid="so-cust-edit-master-link"
                  >
                    Edit in Customer Master →
                  </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Customer</div>
                    <div className="font-semibold text-slate-800 mt-0.5">{custMap[form.customer_id].name}</div>
                    <div className="mono text-[11px] text-slate-500">{custMap[form.customer_id].code}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Contact</div>
                    <div className="text-slate-700 mt-0.5">{custMap[form.customer_id].contact || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Mobile</div>
                    <div className="text-slate-700 mt-0.5 mono">{custMap[form.customer_id].phone || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</div>
                    <div className="text-slate-700 mt-0.5 break-all">{custMap[form.customer_id].email || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><FileTextIcon className="w-3 h-3" /> GSTIN</div>
                    <div className="mono text-slate-700 mt-0.5">{custMap[form.customer_id].gstin || "—"}</div>
                  </div>
                  <div className="col-span-1 lg:col-span-3">
                    <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Billing Address</div>
                    <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{composeAddress(custMap[form.customer_id], "billing") || "—"}</div>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  Address fields below are pre-filled. You can override them per-order without modifying the master record.
                </div>
              </section>
            )}

            {/* Addresses */}
            <section>
              <div className="text-sm font-semibold text-slate-700 mb-2">Addresses</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Billing Address</Label>
                  <Textarea rows={3} value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} data-testid="so-billing-addr" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Shipping Address</Label>
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                      <Checkbox checked={form.same_as_billing} onCheckedChange={(v) => setForm({ ...form, same_as_billing: !!v })} data-testid="so-same-addr" />
                      Shipping address same as billing address
                    </label>
                  </div>
                  <Textarea
                    rows={3}
                    disabled={form.same_as_billing}
                    value={form.same_as_billing ? form.billing_address : form.shipping_address}
                    onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                    data-testid="so-shipping-addr"
                  />
                </div>
              </div>
            </section>

            {/* Customer service contact */}
            <section>
              <div className="text-sm font-semibold text-slate-700 mb-2">Customer Service Contact</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Contact Person</Label>
                  <Input value={form.customer_service.contact_person} onChange={(e) => setForm({ ...form, customer_service: { ...form.customer_service, contact_person: e.target.value } })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.customer_service.email} onChange={(e) => setForm({ ...form, customer_service: { ...form.customer_service, email: e.target.value } })} data-testid="so-cs-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.customer_service.phone} onChange={(e) => setForm({ ...form, customer_service: { ...form.customer_service, phone: e.target.value } })} />
                </div>
              </div>
            </section>

            {/* Issued By */}
            <section data-testid="so-issuer-section">
              <div className="text-sm font-semibold text-slate-700 mb-2">Issued By</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Issuer</Label>
                  <Select value={form.issuer_id || user?.id || ""} onValueChange={(v) => setForm({ ...form, issuer_id: v })}>
                    <SelectTrigger data-testid="so-issuer-select"><SelectValue placeholder="Select issuer" /></SelectTrigger>
                    <SelectContent>
                      {(users.length > 0 ? users : [user]).filter(Boolean).map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}{u.designation ? ` — ${u.designation}` : ""} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {users.length === 0 && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Defaults to you ({user?.name}). To add more issuers, ask an admin to invite users in <button type="button" onClick={() => navigate("/users")} className="underline">Users & Roles</button>.
                    </div>
                  )}
                </div>
                {(() => {
                  const iss = (users.find((u) => u.id === form.issuer_id)) || user;
                  if (!iss) return null;
                  return (
                    <div className="bg-slate-50/80 border border-slate-200 rounded-md p-3 text-xs" data-testid="so-issuer-preview">
                      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Will appear on the document</div>
                      <div className="font-semibold text-slate-800">{iss.name}</div>
                      <div className="text-slate-600">{iss.designation || <span className="italic text-slate-400">No designation set</span>}</div>
                      <div className="text-slate-500 mono">{iss.phone || "—"} · {iss.email}</div>
                      {iss.signature_url && <img src={iss.signature_url} alt="signature" style={{ height: 32, marginTop: 6 }} />}
                    </div>
                  );
                })()}
              </div>
            </section>

            {/* GST treatment badge (intra vs inter) */}
            {form.customer_id && (
              <section className={`rounded-md p-3 text-xs flex items-center justify-between gap-3 ${
                formGstType === "intra" ? "bg-emerald-50 border border-emerald-200" :
                formGstType === "inter" ? "bg-amber-50 border border-amber-200" :
                "bg-slate-100 border border-slate-200"
              }`} data-testid="so-gst-banner">
                <div>
                  <div className="font-semibold uppercase tracking-wide text-[10px] text-slate-600">GST Treatment</div>
                  <div className="text-slate-800 mt-0.5">
                    {formGstType === "intra" && <>Intra-State — applies <strong>CGST + SGST</strong></>}
                    {formGstType === "inter" && <>Inter-State — applies <strong>IGST only</strong></>}
                    {formGstType === "unknown" && <>Undetermined — set <button type="button" onClick={() => navigate("/settings")} className="underline">Company State</button> in Settings and ensure customer has a billing state.</>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Customer State: <span className="mono">{custMap[form.customer_id]?.billing_state || "—"}</span> · Company State: <span className="mono">{companyState || "—"}</span>
                  </div>
                </div>
                {gstRates.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase text-slate-500">Apply to all lines</span>
                    <Select onValueChange={(v) => applyGstRateToAll(parseFloat(v))}>
                      <SelectTrigger className="h-7 w-28 text-xs" data-testid="so-apply-gst-all"><SelectValue placeholder="Pick rate" /></SelectTrigger>
                      <SelectContent>
                        {gstRates.map((g) => <SelectItem key={g.id} value={String(g.gst_pct)}>{g.name} ({g.gst_pct}%)</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </section>
            )}

            {/* Lines */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-700">Line Items</div>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="so-add-line"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
              </div>
              <div className="space-y-3">
                {form.lines.map((ln, idx) => {
                  const it = itemMap[ln.item_id];
                  return (
                    <div key={idx} className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-5">
                          <Label className="text-[10px] uppercase">Item *</Label>
                          <LineItemPicker
                            value={ln.item_id}
                            items={fgItems}
                            onPick={(v) => {
                              const item = items.find((x) => x.id === v);
                              setLine(idx, { item_id: v, uom: item?.uom || ln.uom, price: ln.price || (form.currency === "INR" ? item?.cost || 0 : 0) });
                            }}
                            testId={`so-line-item-${idx}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">HSN Code</Label>
                          <Input value={ln.hsn_code} onChange={(e) => setLine(idx, { hsn_code: e.target.value })} placeholder={it?.hsn_code || "7608"} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">UOM</Label>
                          <Select value={ln.uom} onValueChange={(v) => setLine(idx, { uom: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {UOMS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">Qty *</Label>
                          <Input type="number" value={ln.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} />
                          <StockHint pos={formStock.items?.[ln.item_id]} requested={formRequested[ln.item_id]} mode={form.stock_blocked ? formStock.so_stock_check : "info"} testid={`so-line-stock-${idx}`} />
                        </div>
                        <div className="col-span-1">
                          <Button variant="ghost" size="icon" onClick={() => rmLine(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-12 gap-2 mt-2">
                        <div className="col-span-3">
                          <Label className="text-[10px] uppercase">Rate ({currencySymbol(form.currency)})</Label>
                          <Input type="number" value={ln.price} onChange={(e) => setLine(idx, { price: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">Disc %</Label>
                          <Input type="number" value={ln.discount_pct} onChange={(e) => setLine(idx, { discount_pct: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">GST Rate</Label>
                          <Select onValueChange={(v) => applyGstRateToLine(idx, parseFloat(v))}>
                            <SelectTrigger className="h-9" data-testid={`so-line-gst-rate-${idx}`}><SelectValue placeholder="Pick" /></SelectTrigger>
                            <SelectContent>
                              {gstRates.length === 0 && <SelectItem value="0" disabled>No rates configured</SelectItem>}
                              {gstRates.map((g) => <SelectItem key={g.id} value={String(g.gst_pct)}>{g.gst_pct}% — {g.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Label className="text-[10px] uppercase">CGST</Label>
                          <Input type="number" value={ln.cgst_pct} disabled={formGstType === "inter"} onChange={(e) => setLine(idx, { cgst_pct: e.target.value })} />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-[10px] uppercase">SGST</Label>
                          <Input type="number" value={ln.sgst_pct} disabled={formGstType === "inter"} onChange={(e) => setLine(idx, { sgst_pct: e.target.value })} />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-[10px] uppercase">IGST</Label>
                          <Input type="number" value={ln.igst_pct} disabled={formGstType === "intra"} onChange={(e) => setLine(idx, { igst_pct: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">Delivery Time</Label>
                          <Input value={ln.delivery_time} onChange={(e) => setLine(idx, { delivery_time: e.target.value })} placeholder="2 weeks" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">Delivery Date</Label>
                          <Input type="date" value={ln.delivery_date || ""} disabled={!!ln.partial_delivery}
                            title={ln.partial_delivery ? "Set by the last partial delivery below" : undefined}
                            onChange={(e) => setLine(idx, { delivery_date: e.target.value })} data-testid={`so-edit-line-delivery-${idx}`} />
                        </div>
                        <div className="col-span-2 flex items-end justify-end">
                          <div className="text-xs text-slate-500 mono">
                            Line: {currencySymbol(form.currency)} {(() => {
                              const qty = Number(ln.qty || 0);
                              const price = Number(ln.price || 0);
                              const g = qty * price;
                              const d = g * Number(ln.discount_pct || 0) / 100;
                              const t = g - d;
                              const tax = t * (Number(ln.cgst_pct || 0) + Number(ln.sgst_pct || 0) + Number(ln.igst_pct || 0)) / 100;
                              return (t + tax).toLocaleString(undefined, { maximumFractionDigits: 2 });
                            })()}
                          </div>
                        </div>
                      </div>
                      <PartialDeliveryEditor line={ln} onChange={(patch) => setLine(idx, patch)} testid={`so-line-partial-${idx}`} />
                    </div>
                  );
                })}
              </div>
            </section>
            {/* Note: the Item picker above is a searchable Popover/Command combobox,
                not a plain <Select>. Radix's Select always mounts a hidden native
                <select> with one <option> per entry (for iOS/accessibility) even
                while closed — with the full FG item catalog repeated on every line,
                a multi-line Transfer Order made the Edit sheet noticeably slow to
                open. The combobox only renders its option list while its own
                popover is open, same as the customer picker and the item picker on
                the "Raise Transfer Order" page. */}

            {/* Charges & discounts */}
            <section className="grid grid-cols-3 gap-3">
              <div>
                <Label>Additional Charges Label</Label>
                <Input value={form.additional_charges_label} onChange={(e) => setForm({ ...form, additional_charges_label: e.target.value })} placeholder="Freight / Packing" />
              </div>
              <div>
                <Label>Additional Charges ({currencySymbol(form.currency)})</Label>
                <Input type="number" value={form.additional_charges} onChange={(e) => setForm({ ...form, additional_charges: e.target.value })} />
              </div>
              <div>
                <Label>Header Discount ({currencySymbol(form.currency)})</Label>
                <Input type="number" value={form.header_discount} onChange={(e) => setForm({ ...form, header_discount: e.target.value })} />
              </div>
            </section>

            {/* Terms & Bank */}
            <section className="grid grid-cols-2 gap-3">
              <div>
                <Label>Terms & Conditions</Label>
                <Select value={form.terms_id || "__none"} onValueChange={(v) => setForm({ ...form, terms_id: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="so-terms-select"><SelectValue placeholder="No terms" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None —</SelectItem>
                    {terms.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {terms.length === 0 && (
                  <div className="text-[11px] text-amber-700 mt-1">No T&C templates yet. Add some in <button type="button" onClick={() => navigate("/settings")} className="underline">Settings</button>.</div>
                )}
              </div>
              <div>
                <Label>Bank Account</Label>
                <Select value={form.bank_id || "__none"} onValueChange={(v) => setForm({ ...form, bank_id: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="so-bank-select"><SelectValue placeholder="No bank" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None —</SelectItem>
                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · A/c {b.account_number}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </section>

            {/* Live totals */}
            <section className="bg-blue-50/50 border border-blue-100 rounded-md p-4">
              <div className="text-sm font-semibold text-slate-700 mb-2">Computed Totals</div>
              <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-xs">
                <div className="flex justify-between"><span>Subtotal (taxable)</span><span className="mono">{formatMoney(formTotals.subtotal, form.currency, { decimals: 2 })}</span></div>
                <div className="flex justify-between"><span>CGST</span><span className="mono">{formatMoney(formTotals.cgst, form.currency, { decimals: 2 })}</span></div>
                <div className="flex justify-between"><span>SGST</span><span className="mono">{formatMoney(formTotals.sgst, form.currency, { decimals: 2 })}</span></div>
                <div className="flex justify-between"><span>IGST</span><span className="mono">{formatMoney(formTotals.igst, form.currency, { decimals: 2 })}</span></div>
                <div className="flex justify-between"><span>Line discounts</span><span className="mono">- {formatMoney(formTotals.discount, form.currency, { decimals: 2 })}</span></div>
                <div className="flex justify-between"><span>Other charges</span><span className="mono">{formatMoney(formTotals.charges, form.currency, { decimals: 2 })}</span></div>
                <div className="flex justify-between"><span>Header discount</span><span className="mono">- {formatMoney(formTotals.headerDisc, form.currency, { decimals: 2 })}</span></div>
                <div className="flex justify-between"><span>Round off</span><span className="mono">{formatMoney(formTotals.roundOff, form.currency, { decimals: 2 })}</span></div>
                <div className="flex justify-between font-bold text-slate-800"><span>Grand Total</span><span className="mono" data-testid="so-form-grand-total">{formatMoney(formTotals.grand, form.currency, { withCode: true })}</span></div>
              </div>
            </section>
          </div>

          <SheetFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="create-so-btn">
              {revising ? "Save Revision" : editingId ? "Save Changes" : "Create Transfer Order"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Email Transfer Order {toNo(selected?.code)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>To *</Label>
              <Input type="email" value={emailPayload.to_email} onChange={(e) => setEmailPayload({ ...emailPayload, to_email: e.target.value })} data-testid="so-email-to" />
            </div>
            <div>
              <Label>Cc</Label>
              <Input
                type="email"
                multiple
                placeholder="comma-separated addresses"
                value={emailPayload.cc_email}
                onChange={(e) => setEmailPayload({ ...emailPayload, cc_email: e.target.value })}
                data-testid="so-email-cc"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Auto-filled with the Transfer Order creator's email — edit or clear as needed. Separate multiple addresses with commas.
              </div>
            </div>
            <div>
              <Label>Bcc</Label>
              <Input
                type="email"
                multiple
                placeholder="comma-separated addresses"
                value={emailPayload.bcc_email}
                onChange={(e) => setEmailPayload({ ...emailPayload, bcc_email: e.target.value })}
                data-testid="so-email-bcc"
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={emailPayload.subject} onChange={(e) => setEmailPayload({ ...emailPayload, subject: e.target.value })} />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={3} value={emailPayload.message} onChange={(e) => setEmailPayload({ ...emailPayload, message: e.target.value })} />
            </div>

            {/* Email history/audit trail — Requirement 7. Send Email always
                stays clickable above regardless of what this list shows. */}
            {emailHistory.length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Email History</div>
                <div className="max-h-48 overflow-y-auto space-y-1.5" data-testid="so-email-history">
                  {emailHistory.map((h) => (
                    <div key={h.id} className="flex items-center justify-between gap-2 text-xs" title={h.error || ""}>
                      <div className="min-w-0">
                        <span className="text-slate-700">{h.to}</span>
                        <span className="text-slate-400 ml-1.5">{h.sent_at ? new Date(h.sent_at).toLocaleString() : ""}</span>
                        {h.status !== "sent" && h.error && (
                          <div className="text-[10px] text-red-600 break-words">{h.error}</div>
                        )}
                        {(h.provider || h.from_email || h.server_reply) && (
                          <div className="text-[10px] text-slate-500 break-words">
                            via {h.provider === "smtp" ? "company mailbox (SMTP)" : h.provider === "resend" ? "Resend" : "no provider"}
                            {h.from_email ? ` · from ${h.from_email}` : ""}
                            {h.server_reply ? ` · server: ${h.server_reply}` : ""}
                          </div>
                        )}
                        {h.status === "sent" && (
                          <button type="button" className="text-[10px] text-blue-700" onClick={() => checkEmailDelivery(h.id)} data-testid={`so-email-check-${h.id}`}>
                            Check delivery
                          </button>
                        )}
                        {emailDelivery[h.id] && (
                          <div className={`text-[10px] break-words ${["delivered", "handed_to_mailbox"].includes(emailDelivery[h.id].delivery) ? "text-emerald-700" : emailDelivery[h.id].delivery === "unknown" ? "text-amber-700" : "text-red-600"}`}>
                            Delivery: {(emailDelivery[h.id].delivery || "unknown").replace(/_/g, " ")}{emailDelivery[h.id].detail ? ` — ${emailDelivery[h.id].detail}` : ""}
                          </div>
                        )}
                      </div>
                      <span className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${h.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                        {h.status === "sent" ? "Sent" : "Failed"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="so-email-send"><Mail className="w-4 h-4 mr-1.5" />Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master / detail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>Orders</span><span className="text-xs text-slate-500 mono">{visibleList.length}/{list.length}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-100 space-y-2">
            <Input
              placeholder="Search by TO code, customer, PO ref, item..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="so-search-input"
            />
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-[10px] uppercase text-slate-500">Order date from</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-8 text-xs mt-0.5"
                  data-testid="so-date-from-input"
                />
              </div>
              <div className="flex-1">
                <Label className="text-[10px] uppercase text-slate-500">To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-8 text-xs mt-0.5"
                  data-testid="so-date-to-input"
                />
              </div>
              {(dateFrom || dateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-slate-500"
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  data-testid="so-date-filter-clear"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {visibleList.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-sos">No transfer orders match.</div>}
            {visibleList.map((so) => {
              const lineTotal = (so.lines || []).reduce((s, l) => s + (l.qty || 0) * (l.price || 0), 0);
              return (
                <button key={so.id} onClick={() => setSelected(so)} data-testid={`so-row-${so.code}`}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === so.id ? "bg-blue-50/60" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{toNo(so.code)}</div>
                    <span className="flex items-center gap-1">
                      <OrderStockBadge info={orderStock[so.id]} testid={`so-stock-badge-${so.code}`} />
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[so.status] || "bg-slate-100"}`}>{so.status.replace("_", " ")}</span>
                    </span>
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-800">{so.customer_code || custMap[so.customer_id]?.code} — {so.customer_name || custMap[so.customer_id]?.name || "—"}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {so.lines.length} line(s) · {formatMoney(lineTotal, docCurrency(so))}
                    {orderDate(so) && <> · <span className="mono" data-testid={`so-row-date-${so.code}`}>{new Date(orderDate(so)).toLocaleDateString()}</span></>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="so-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <Factory className="w-10 h-10 text-slate-300" /> Select a transfer order to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.revision_number ? (selected.revision_no || revisionNo(toNo(selected.code), selected.revision_number)) : toNo(selected.code)}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {selected.customer_name || custMap[selected.customer_id]?.name || "—"}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1">
                    PO ref: <span className="mono">{selected.po_reference || "—"}</span>
                    {selected.source_quotation_code && <> · <span className="text-purple-700">From Quotation <span className="mono">{selected.source_quotation_code}</span></span></>}
                    {selected.so_date && <> · Order Date: <span className="mono">{new Date(selected.so_date).toLocaleDateString()}</span></>}
                    {selected.estimated_completion_date && <> · Est. Completion: <span className="mono">{new Date(selected.estimated_completion_date).toLocaleDateString()}</span></>}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[selected.status] || "bg-slate-100"}`}>{selected.status.replace("_", " ")}</span>
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const res = await api.get(`/sales-orders/${selected.id}/pdf`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const a = document.createElement("a"); a.href = url; a.download = `TransferOrder-${toNo(selected.code)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                    toast.success(`Downloaded ${toNo(selected.code)}.pdf`);
                  } catch { toast.error("PDF download failed"); }
                }} data-testid="so-pdf-btn"><Download className="w-4 h-4 mr-1.5" /> Download PDF</Button>
              </div>

              <div className="flex items-center gap-2 flex-wrap" data-testid="so-actions">
                {selected.status === "order_raised" && ["admin", "planner", "production_manager"].includes(user?.role) && (
                  <Button
                    onClick={() => navigate("/production-approvals")}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="so-goto-approvals-btn"
                  >
                    <Factory className="w-4 h-4 mr-1.5" /> Review in Production Approvals
                  </Button>
                )}
                {!["delivered", "cancelled"].includes(selected.status) && !selected.packing_locked && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(selected)} data-testid="so-edit-btn"><FileEdit className="w-4 h-4 mr-1.5" />Edit</Button>
                )}
                {selected.status === "draft" && selected.revision_number > 0 && canRevise(selected) && !selected.packing_locked && (
                  <Button onClick={() => callAction(selected, "resubmit", `${toNo(selected.code)} re-submitted for Production Approval`)} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="so-resubmit-btn">
                    <Factory className="w-4 h-4 mr-1.5" /> Re-submit for Approval
                  </Button>
                )}
                {selected.status === "draft" && !(selected.revision_number > 0) && canConfirm && (
                  <Button onClick={() => callAction(selected, "confirm", `${toNo(selected.code)} confirmed`)} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="so-confirm-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm Order
                  </Button>
                )}
                {canRevise(selected) && !selected.packing_locked && ["order_raised", "confirmed", "in_production", "ready"].includes(selected.status) && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(selected, true)} disabled={busy} data-testid="so-revise-btn">
                    <FileEdit className="w-4 h-4 mr-1.5" /> Revise TO
                  </Button>
                )}
                <RevisionHistoryButton record={selected} baseNo={toNo(selected.code)} testid="so-revision-history" />
                {canCancel(selected) && !selected.packing_locked && !["delivered", "cancelled", "revised"].includes(selected.status) && (
                  <Button variant="outline" onClick={() => {
                    const reason = window.prompt(`Cancel ${toNo(selected.code)}? Its Sales Order, draft Packing Slips and planned Work Orders are cancelled too.\n\nReason:`);
                    if (reason === null) return;
                    callAction(selected, "cancel", `${toNo(selected.code)} cancelled`, { reason });
                  }} disabled={busy} data-testid="so-cancel-btn">
                    <XCircle className="w-4 h-4 mr-1.5 text-red-500" /> Cancel TO
                  </Button>
                )}
                {selected.packing_locked && !["delivered", "cancelled"].includes(selected.status) && (
                  <span className="text-[11px] text-slate-500" data-testid="so-packing-locked">
                    Packing completed — revision/cancellation locked ({selected.packing_lock_reason})
                  </span>
                )}
                <Button variant="outline" size="sm" onClick={printSO} data-testid="so-print-btn">
                  <Printer className="w-4 h-4 mr-1.5" />Print
                </Button>
                <Button variant="outline" size="sm" onClick={openEmail} data-testid="so-email-btn">
                  <Mail className="w-4 h-4 mr-1.5" />Email
                </Button>
                <OrderEmailStatus order={selected} resendPath={`/sales-orders/${selected.id}/resend-email`}
                  label={`Order ${toNo(selected.code)}`} myEmail={user?.email} onDone={load} testid="so-auto-email" />
                {(selected.status === "draft" || selected.status === "order_raised") && (
                  <Button variant="ghost" size="icon" onClick={() => remove(selected)} className="ml-auto" data-testid="so-delete-btn">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>

              {/* Addresses block */}
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
                  <div className="font-semibold text-slate-700 mb-1">Billed To{selected.bill_to?.label ? <span className="font-normal text-slate-500"> · {selected.bill_to.label}</span> : null}</div>
                  <div className="text-slate-700 whitespace-pre-wrap">{selected.billing_address || custMap[selected.customer_id]?.address || "—"}</div>
                  <div className="text-slate-500 mt-1">GSTIN: <span className="mono">{selected.bill_to?.gstin || selected.customer_gstin || custMap[selected.customer_id]?.gstin || "—"}</span></div>
                </div>
                <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
                  <div className="font-semibold text-slate-700 mb-1">Shipped To{selected.ship_to?.label ? <span className="font-normal text-slate-500"> · {selected.ship_to.label}</span> : null}</div>
                  <div className="text-slate-700 whitespace-pre-wrap">{selected.same_as_billing ? (selected.billing_address || custMap[selected.customer_id]?.address || "—") : (selected.shipping_address || "—")}</div>
                </div>
              </div>

              {selected.customer_service && (selected.customer_service.contact_person || selected.customer_service.email || selected.customer_service.phone) && (
                <div className="text-xs text-slate-600">
                  <span className="font-semibold">Customer service:</span> {selected.customer_service.contact_person} · {selected.customer_service.email} · {selected.customer_service.phone}
                </div>
              )}

              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic">{selected.notes}</div>
              )}

              {/* Lines table with GST */}
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Lines</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                        <th className="py-2 px-3">Part No</th>
                        <th className="py-2 px-3">Item</th>
                        <th className="py-2 px-3">HSN</th>
                        <th className="py-2 px-3">Qty</th>
                        <th className="py-2 px-3">Rate</th>
                        <th className="py-2 px-3">Disc</th>
                        <th className="py-2 px-3">GST</th>
                        <th className="py-2 px-3">Amount</th>
                        <th className="py-2 px-3">Delivery Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.lines.map((l, idx) => {
                        const computed = selectedTotals?.lines_computed?.[idx];
                        return (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="py-2 px-3 mono">{itemMap[l.item_id]?.code || l.part_code || "—"}</td>
                            <td className="py-2 px-3 text-slate-700">{itemMap[l.item_id]?.name || l.part_name}</td>
                            <td className="py-2 px-3 mono">{l.hsn_code || "—"}</td>
                            <td className="py-2 px-3 mono">{l.qty} {l.uom}</td>
                            <td className="py-2 px-3 mono">{formatMoney(Number(l.price), docCurrency(selected))}</td>
                            <td className="py-2 px-3 mono">{l.discount_pct || 0}%</td>
                            <td className="py-2 px-3 mono">{(l.cgst_pct || 0) + (l.sgst_pct || 0) + (l.igst_pct || 0)}%</td>
                            <td className="py-2 px-3 mono font-semibold">{formatMoney((computed?.line_total ?? l.qty * l.price), docCurrency(selected))}</td>
                            <td className="py-2 px-3 text-slate-700 mono text-xs" data-testid={`so-line-delivery-${idx}`}>
                              {l.delivery_date ? new Date(l.delivery_date).toLocaleDateString() : <span className="text-rose-500">— not set</span>}
                              <PartialDeliveryList line={l} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <OrderStockPanel
                info={selectedStock.info}
                loading={selectedStock.loading}
                error={selectedStock.error}
                itemMap={itemMap}
                soId={selected.id}
                onChanged={() => setStockTick((t) => t + 1)}
                testid="so-stock"
              />

              {/* Totals summary */}
              {selectedTotals && (
                <div className="flex justify-end">
                  <div className="bg-blue-50/50 border border-blue-100 rounded-md p-3 w-80 text-xs">
                    <div className="flex justify-between py-0.5"><span>Subtotal</span><span className="mono">{formatMoney(selectedTotals.subtotal, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>CGST</span><span className="mono">{formatMoney(selectedTotals.cgst_total, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>SGST</span><span className="mono">{formatMoney(selectedTotals.sgst_total, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>IGST</span><span className="mono">{formatMoney(selectedTotals.igst_total, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>Other charges</span><span className="mono">{formatMoney(selectedTotals.additional_charges, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>Discount</span><span className="mono">- {formatMoney(selectedTotals.header_discount, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>Round off</span><span className="mono">{formatMoney(selectedTotals.round_off, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-1 mt-1 border-t border-blue-200 font-bold"><span>Grand Total</span><span className="mono" data-testid="so-detail-grand">{formatMoney(selectedTotals.grand_total, docCurrency(selected), { withCode: true })}</span></div>
                  </div>
                </div>
              )}

              <div className="text-xs text-slate-500">
                Flow: <span className="mono">draft → confirmed → in_production → delivered</span>. Dispatching a Delivery Challan auto-decrements FG inventory and updates delivered qty.
              </div>

              {/* GST + Issued By footer (mirrors what prints/emails) */}
              <div className="border-t border-slate-200 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div data-testid="so-detail-gst">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">GST Treatment</div>
                  <div className="mt-1">
                    {selected.gst_type === "intra" && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] uppercase tracking-wide">Intra-State · CGST + SGST</span>}
                    {selected.gst_type === "inter" && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] uppercase tracking-wide">Inter-State · IGST only</span>}
                    {(!selected.gst_type || selected.gst_type === "unknown") && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wide">Undetermined</span>}
                  </div>
                  <div className="text-slate-500 mt-1">
                    Customer State: <span className="mono">{selected.customer_state || "—"}</span> · Company State: <span className="mono">{selected.company_state || "—"}</span>
                  </div>
                </div>
                <div data-testid="so-detail-issuer">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Issued By</div>
                  {(() => {
                    const iss = selected.issuer_snapshot || {};
                    return (
                      <div className="mt-1">
                        <div className="font-semibold text-slate-800">{iss.name || "—"}</div>
                        <div className="text-slate-600">{iss.designation || ""}</div>
                        <div className="text-slate-500">{iss.phone || ""}{iss.email ? ` · ${iss.email}` : ""}</div>
                        {iss.signature_url && <img src={iss.signature_url} alt="signature" style={{ height: 28, marginTop: 4 }} />}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Audit trail */}
              <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2" data-testid="so-detail-audit">
                Created by <span className="mono">{selected.created_by || "—"}</span> on {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
                {selected.confirmed_by && <> · Confirmed by <span className="mono">{selected.confirmed_by}</span> on {selected.confirmed_at ? new Date(selected.confirmed_at).toLocaleString() : "—"}</>}
                {selected.cancelled_at && <> · Cancelled on {new Date(selected.cancelled_at).toLocaleString()}</>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Searchable item combobox for a line's Item field — a Popover/Command pair
// rather than a plain <Select>, so the full FG item catalog is only rendered
// while this specific line's picker is open (see the comment above its call
// site). Mirrors the LineItemPicker on the "Raise Transfer Order" page.
// Ranks/filters items ourselves instead of leaving it to cmdk's default fuzzy
// scorer, which happily surfaces unrelated part numbers (e.g. typing the full
// "AP24400016" would still show "AP24200016", "AP24300016", …) because those
// share enough characters in order. A part number the user typed in full
// should stand alone; anything shorter falls back to a predictable
// prefix-then-substring match on code/name.
function filterFgItems(items, search) {
  const q = search.trim().toLowerCase();
  if (!q) return items;
  const exact = items.find((it) => (it.code || "").toLowerCase() === q);
  if (exact) return [exact];
  const starts = [];
  const contains = [];
  for (const it of items) {
    const code = (it.code || "").toLowerCase();
    const name = (it.name || "").toLowerCase();
    if (code.startsWith(q)) starts.push(it);
    else if (code.includes(q) || name.includes(q)) contains.push(it);
  }
  return [...starts, ...contains];
}

function LineItemPicker({ value, items, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = items.find((it) => it.id === value);
  const filteredItems = useMemo(() => filterFgItems(items, search), [items, search]);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between h-9 font-normal" data-testid={testId}>
          <span className="truncate text-left">{selected ? `${selected.code} — ${selected.name}` : "Pick item"}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[380px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by part number or name…"
            value={search}
            onValueChange={setSearch}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty>No items found</CommandEmpty>
            <CommandGroup>
              {filteredItems.map((it) => (
                <CommandItem
                  key={it.id}
                  value={it.id}
                  onSelect={() => { onPick(it.id); setOpen(false); }}
                  data-testid={`${testId}-opt-${it.code}`}
                >
                  <Check className={`w-3.5 h-3.5 mr-2 ${value === it.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{it.code}</span>
                    <span className="text-[11px] text-slate-500 truncate">{it.name}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
